import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, X, Sparkles } from 'lucide-react';
import { useAiSuggestions } from '../hooks/useAiSuggestions';
import { useAiNormalize } from '../hooks/useAiNormalize';
import AiSuggestionChips from './AiSuggestionChips';
import AINormalizationPanel from './AINormalizationPanel';

const CATEGORIES = [
  { id: 'financieel', name: 'Financieel & Belasting' },
  { id: 'contracten', name: 'Contracten & Huur' },
  { id: 'leveranciers', name: 'Leveranciers & Inkoop' },
  { id: 'techniek', name: 'Techniek & Apparatuur' },
  { id: 'voertuigen', name: 'Voertuigen' },
  { id: 'vastgoed', name: 'Vastgoed & Facilitair' },
  { id: 'veiligheid', name: 'Veiligheid & Compliance' },
  { id: 'milieu', name: 'Milieu & Infrastructuur' },
  { id: 'nieuwbouw', name: 'Nieuwbouw & Verbouwing' },
  { id: 'overnames', name: 'Overnames' },
  { id: 'automatisering', name: 'Automatisering' },
];

export default function QuickAdd() {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ location_id: '', title: '', content: '', category: '' });
  const [submitting, setSubmitting] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const { authAxios } = useAuth();
  const { suggestions, loading: aiLoading, debouncedFetch, dismiss: aiDismiss, reset: aiReset } = useAiSuggestions();
  const { result: normalizeResult, loading: normalizeLoading, error: normalizeError, normalize, reset: normalizeReset } = useAiNormalize();
  const [normalizeTriggered, setNormalizeTriggered] = useState(false);


  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener('open-quick-add', handleOpen);
    return () => window.removeEventListener('open-quick-add', handleOpen);
  }, []);
  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const res = await api.get('/locations');
        setLocations(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [authAxios]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Geef een korte titel op');
      return;
    }
    setSubmitting(true);
    try {
      const api = authAxios();
      const locationId = form.location_id || 'all';
      await api.post('/entries', {
        location_id: locationId,
        title: form.title,
        content: form.content,
        category: form.category || undefined,
        entry_status: 'active',
        ai_categorized: aiApplied,
        source: 'manual',
      });
      toast.success(form.category ? 'Item toegevoegd' : 'Opgeslagen — de AI sorteert het zo in.');
      setForm({ location_id: '', title: '', content: '', category: '' });
      setAiApplied(false);
      aiReset();
      normalizeReset();
      setNormalizeTriggered(false);
      setOpen(false);
    } catch (e) {
      toast.error('Fout bij opslaan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm(prev => ({ ...prev, title }));
    debouncedFetch(title, form.content);
  };

  const handleApplySuggestion = (type, value) => {
    if (type === 'category') {
      setForm(prev => ({ ...prev, category: value }));
      setAiApplied(true);
      toast.success('Categorie ingevuld door AI');
    } else if (type === 'location') {
      setForm(prev => ({ ...prev, location_id: value }));
      setAiApplied(true);
      toast.success('Locatie ingevuld door AI');
    } else if (type === 'tag') {
      toast.success(`Tag "${value}" genoteerd`);
    }
  };

  return (
    <>
      {/* FAB */}
      <button
        data-testid="quick-add-fab"
        onClick={() => setOpen(true)}
        className="hidden md:flex fixed bottom-20 right-6 z-40 h-12 w-12 rounded-full bg-[#2E75B6] text-white shadow-lg hover:shadow-xl hover:-translate-y-[1px] active:scale-[0.97] transition-[box-shadow,background-color,transform] duration-200 flex items-center justify-center"
        title="Snel toevoegen"
      >
        <Plus className="w-5 h-5" />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md mx-4 bg-white dark:bg-[#161B22] rounded-2xl border border-slate-200 dark:border-[#30363D] shadow-2xl" data-testid="quick-add-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-[#30363D]">
              <h3 className="font-heading text-base font-semibold text-slate-900 dark:text-[#E6EDF3]">Snel Toevoegen</h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Locatie (optioneel)</label>
                <select
                  data-testid="quick-add-location"
                  value={form.location_id}
                  onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
                >
                  <option value="">Geen / alle locaties (AI vult aan)</option>
                  <option value="all">Alle locaties (groepsbreed)</option>
                  {locations.map(loc => (
                    <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Titel *</label>
                <input
                  data-testid="quick-add-title"
                  type="text"
                  value={form.title}
                  onChange={handleTitleChange}
                  placeholder="Korte beschrijving..."
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
                  required
                />
                <AiSuggestionChips
                  suggestions={suggestions}
                  loading={aiLoading}
                  onApply={handleApplySuggestion}
                  onDismiss={aiDismiss}
                  compact
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Inhoud</label>
                <textarea
                  data-testid="quick-add-content"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Notities of details..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Categorie (optioneel)</label>
                <select
                  data-testid="quick-add-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
                >
                  <option value="">Geen categorie (AI vult aan)</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">Zonder categorie sorteert de AI het item automatisch in</p>
              </div>

              {/* AI Normalize */}
              {form.title && form.title.length >= 3 && (
                <div className="space-y-2">
                  {!normalizeResult && !normalizeLoading && (
                    <button
                      type="button"
                      data-testid="quick-add-normalize-trigger"
                      onClick={() => {
                        setNormalizeTriggered(true);
                        normalize(form.title, form.content);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#2E75B6]/30 bg-[#2E75B6]/[0.03] text-[#2E75B6] text-[11px] font-medium hover:bg-[#2E75B6]/10 transition-colors"
                    >
                      <Sparkles className="w-3 h-3" />
                      AI Normalisatie
                    </button>
                  )}
                  <AINormalizationPanel
                    result={normalizeResult}
                    loading={normalizeLoading}
                    error={normalizeError}
                    originalTitle={form.title}
                    originalContent={form.content}
                    compact
                    onApplyAll={(res) => {
                      setForm(prev => ({
                        ...prev,
                        title: res.cleaned_title || prev.title,
                        content: res.cleaned_content || prev.content,
                        category: res.category || prev.category,
                        location_id: res.location || prev.location_id,
                      }));
                      setAiApplied(true);
                      toast.success('AI suggesties overgenomen');
                    }}
                    onApplyField={(field, value) => {
                      if (field === 'title') setForm(prev => ({ ...prev, title: value }));
                      else if (field === 'content') setForm(prev => ({ ...prev, content: value }));
                      else if (field === 'category') setForm(prev => ({ ...prev, category: value }));
                      else if (field === 'location') setForm(prev => ({ ...prev, location_id: value }));
                      setAiApplied(true);
                      toast.success('Overgenomen');
                    }}
                    onDismiss={() => { normalizeReset(); setNormalizeTriggered(false); }}
                    onRetry={() => normalize(form.title, form.content)}
                  />
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#21262D] transition-colors"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  data-testid="quick-add-submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.98] transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
